import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useT, Namespace } from "@racoon/i18n";
import { Button, Input, Label, useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import {
  organizationLabelSchema,
  type OrganizationLabelFormValues,
  type OrganizationLabelMutations,
} from "./organization-label.schema";

const { LocationStatus } = ConnectProto;

interface OrganizationLabelFormProps {
  label: ConnectProto.OrganizationLabel | null;
  mutations: OrganizationLabelMutations;
  onClose: () => void;
}

/** Body of the add / edit label modal. «Удалить» archives the label; an archived one is restored. */
export const OrganizationLabelForm = ({
  label,
  mutations,
  onClose,
}: OrganizationLabelFormProps) => {
  const t = useT();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OrganizationLabelFormValues>({
    resolver: zodResolver(organizationLabelSchema),
    defaultValues: { name: label?.name ?? "" },
    mode: "onTouched",
  });
  const archived = label?.status === LocationStatus.INACTIVE;

  const onSubmit = async ({ name }: OrganizationLabelFormValues) => {
    try {
      if (label) {
        await mutations.update(label.id, { name });
        toast({ title: t(Namespace.Settings, "organizationLabels.toast.saved") });
      } else {
        await mutations.create(name);
        toast({ title: t(Namespace.Settings, "organizationLabels.toast.created") });
      }
      onClose();
    } catch {
      // GlobalErrorToaster shows the API error; keep the modal open for a retry.
    }
  };

  const toggleArchive = async () => {
    if (!label) return;
    try {
      await mutations.update(label.id, {
        status: archived ? LocationStatus.ACTIVE : LocationStatus.INACTIVE,
      });
      toast({
        title: t(
          Namespace.Settings,
          archived ? "organizationLabels.toast.restored" : "organizationLabels.toast.archived",
        ),
      });
      onClose();
    } catch {
      // GlobalErrorToaster shows the API error.
    }
  };

  const nameError = errors.name?.message;

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex flex-col gap-1 p-4">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="organization-label-name" className="font-medium text-text-body">
            {t(Namespace.Settings, "organizationLabels.dialog.name")}
          </Label>
          <Input
            id="organization-label-name"
            {...register("name")}
            maxLength={255}
            autoFocus
            aria-invalid={!!nameError}
            aria-describedby={nameError ? "organization-label-name-error" : undefined}
            className="h-8 w-[220px]"
          />
        </div>
        {nameError && (
          <p id="organization-label-name-error" className="self-end text-xs text-text-error">
            {t(Namespace.Settings, "organizationLabels.dialog.nameRequired")}
          </p>
        )}
      </div>

      <div
        className={`flex border-t border-slate-200 p-4 ${label ? "justify-between" : "justify-end"}`}
      >
        {label && (
          <Button
            type="button"
            variant={archived ? "outline" : "solidRed"}
            onClick={toggleArchive}
            disabled={isSubmitting}
          >
            {archived
              ? t(Namespace.Settings, "organizationLabels.dialog.restore")
              : t(Namespace.Buttons, "delete")}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting} className="!bg-blue-600 hover:!bg-blue-700">
          {label
            ? t(Namespace.Buttons, "save")
            : t(Namespace.Settings, "organizationLabels.dialog.createButton")}
        </Button>
      </div>
    </form>
  );
};
